import { fetchTotpStatus, type TotpStatus } from "@/api/auth";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/api/client";
import { formatApiError } from "@/lib/formatApiError";
import { useStorageList } from "@/features/storages/hooks/useStorages";
import {
  isStorageIntegratedAvailable,
  resolveStorageIntegratedAvailabilityCode,
  type StorageAvailabilityCode,
} from "@/features/storages/storage-ux";
import { AlertTriangle, HardDrive, Link2 } from "lucide-react";
import { PlanStatusBanner } from "@/features/billing/PlanStatusBanner";
import type { AppBindingSummary } from "@/types/api";
import { PageHeader } from "@/components/layout/PageHeader";

interface DashboardData {
  appBindings: AppBindingSummary[];
  isLoading: boolean;
  error: string | null;
}

const DASHBOARD_AVAILABILITY_CODES: StorageAvailabilityCode[] = [
  "available",
  "setup_incomplete",
  "needs_action",
  "needs_check",
  "unavailable",
];

const STORAGE_ALERT_AVAILABILITY_CODES: Exclude<
  StorageAvailabilityCode,
  "available"
>[] = ["setup_incomplete", "needs_action", "needs_check", "unavailable"];

type AppBindingAvailabilityState = NonNullable<
  AppBindingSummary["availabilityState"]
>;
type AppBindingAlertAvailabilityState = Exclude<
  AppBindingAvailabilityState,
  "available"
>;

const APP_ALERT_AVAILABILITY_CODES: AppBindingAlertAvailabilityState[] = [
  "setup_incomplete",
  "needs_action",
  "needs_check",
  "unavailable",
];

const APP_SUMMARY_AVAILABILITY_CODES: AppBindingAvailabilityState[] = [
  "available",
  "setup_incomplete",
  "needs_action",
  "needs_check",
  "unavailable",
];

interface AppAvailabilityAlert {
  availabilityState: AppBindingAlertAvailabilityState;
  count: number;
}

function buildAppAvailabilityAlerts(
  appBindings: AppBindingSummary[],
): AppAvailabilityAlert[] {
  const counts = new Map<AppBindingAlertAvailabilityState, number>();
  for (const binding of appBindings) {
    const availabilityState = binding.availabilityState ?? "needs_check";
    if (
      !APP_ALERT_AVAILABILITY_CODES.includes(
        availabilityState as AppBindingAlertAvailabilityState,
      )
    ) {
      continue;
    }
    const state = availabilityState as AppBindingAlertAvailabilityState;
    counts.set(state, (counts.get(state) ?? 0) + 1);
  }
  return APP_ALERT_AVAILABILITY_CODES.flatMap((availabilityState) => {
    const count = counts.get(availabilityState) ?? 0;
    return count > 0 ? [{ availabilityState, count }] : [];
  });
}

function createAppAvailabilityCounts(): Record<AppBindingAvailabilityState, number> {
  return APP_SUMMARY_AVAILABILITY_CODES.reduce(
    (acc, code) => ({ ...acc, [code]: 0 }),
    {} as Record<AppBindingAvailabilityState, number>,
  );
}

function buildAppAvailabilitySummaries(
  appBindings: AppBindingSummary[],
): Record<AppBindingAvailabilityState, number> {
  const counts = createAppAvailabilityCounts();
  for (const binding of appBindings) {
    const availabilityState = binding.availabilityState ?? "needs_check";
    counts[availabilityState] += 1;
  }
  return counts;
}

function alertTextClassForAvailability(
  availabilityState: StorageAvailabilityCode | AppBindingAlertAvailabilityState,
) {
  return availabilityState === "needs_action" ||
    availabilityState === "unavailable"
    ? "text-destructive"
    : "text-amber-700";
}

export function DashboardPage() {
  const { t } = useTranslation("dashboard");
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const [totpStatus, setTotpStatus] = useState<TotpStatus | null>(null);
  const [totpError, setTotpError] = useState(false);
  useEffect(() => {
    let active = true;
    void fetchTotpStatus().then(value => { if (active) setTotpStatus(value); }, () => { if (active) setTotpError(true); });
    return () => { active = false; };
  }, []);
  const totpWarning = Boolean(totpStatus?.localEnabled && !totpStatus.enabled);
  const {
    items: storages,
    isLoading: isLoadingStorages,
    error: storageListError,
  } = useStorageList({ limit: "100" });
  const [data, setData] = useState<DashboardData>({
    appBindings: [],
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bindingRes =
          await apiClient<{ items: AppBindingSummary[] }>("/kintone-apps?limit=100");
        if (cancelled) return;
        setData({
          appBindings: bindingRes.items,
          isLoading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setData({
          appBindings: [],
          isLoading: false,
          error: formatApiError(err, tc("errors.http.SERVER_ERROR")),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tc]);

  const availabilityCounts = DASHBOARD_AVAILABILITY_CODES.reduce(
    (acc, code) => ({ ...acc, [code]: 0 }),
    {} as Record<StorageAvailabilityCode, number>,
  );
  for (const storage of storages) {
    availabilityCounts[resolveStorageIntegratedAvailabilityCode(storage)] += 1;
  }
  const hasStorageAlerts = STORAGE_ALERT_AVAILABILITY_CODES.some(
    (code) => availabilityCounts[code] > 0,
  );
  const hasAvailableStorage = storages.some(isStorageIntegratedAvailable);
  const appAvailabilityAlerts = buildAppAvailabilityAlerts(data.appBindings);
  const appAvailabilitySummary = buildAppAvailabilitySummaries(data.appBindings);
  const pluginReapplyPendingCount = data.appBindings.filter(
    (binding) => binding.settingReflectionState === "plugin_reapply_pending",
  ).length;
  const pluginUpdateAvailableCount = data.appBindings.filter(
    (binding) => binding.pluginVersionState === "update_available",
  ).length;
  const pluginVersionIncompatibleCount = data.appBindings.filter(
    (binding) =>
      binding.blockedReason === "plugin_version_incompatible" ||
      binding.pluginVersionState === "incompatible",
  ).length;
  const hasBindingAlerts =
    appAvailabilityAlerts.length > 0 ||
    pluginReapplyPendingCount > 0 ||
    pluginUpdateAvailableCount > 0 ||
    pluginVersionIncompatibleCount > 0;
  const alertTargetCount =
    STORAGE_ALERT_AVAILABILITY_CODES.reduce(
      (total, code) => total + availabilityCounts[code],
      0,
    ) +
    appAvailabilityAlerts.reduce((total, alert) => total + alert.count, 0) +
    pluginReapplyPendingCount +
    pluginUpdateAvailableCount +
    pluginVersionIncompatibleCount + (totpWarning ? 1 : 0);
  const hasStorageLoadResult = !isLoadingStorages && !storageListError;
  const hasStorages = hasStorageLoadResult && storages.length > 0;
  const showSummaryCards = isLoadingStorages || Boolean(storageListError) || hasStorages;
  const showStorageOnboarding =
    hasStorageLoadResult && storages.length === 0;
  const showAppBindingOnboarding =
    hasStorageLoadResult &&
    !showStorageOnboarding &&
    hasAvailableStorage &&
    !data.isLoading &&
    !data.error &&
    data.appBindings.length === 0;
  const alertTotalText =
    isLoadingStorages || data.isLoading
      ? t("total.loading")
      : storageListError || data.error || totpError
        ? t("total.unknown")
        : t("total.count", { count: alertTargetCount });
  const storageTotalText = isLoadingStorages
    ? t("total.loading")
    : storageListError
      ? t("total.unknown")
      : t("total.count", { count: storages.length });
  const appTotalText = data.isLoading
    ? t("total.loading")
    : data.error
      ? t("total.unknown")
      : t("total.count", { count: data.appBindings.length });

  return (
    <div className="console-page">
      <div className="mb-6"><PageHeader title={t("title")} /></div>
      <PlanStatusBanner placement="dashboard" className="mb-6" />

      {showStorageOnboarding ? (
        <div className="mb-6 flex flex-col gap-4 rounded-md border border-amber-300 bg-amber-50 text-amber-950 p-5 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-base font-semibold">
              {t("storageOnboarding.title")}
            </h2>
            <p className="max-w-2xl text-sm text-amber-900">
              {t("storageOnboarding.body")}
            </p>
          </div>
          <Button className="bg-amber-300 text-amber-950 hover:bg-amber-400" onClick={() => navigate("/console/storages")}>
            <HardDrive className="mr-2 h-4 w-4" />
            {t("storageOnboarding.cta")}
          </Button>
        </div>
      ) : null}

      {showAppBindingOnboarding ? (
        <div className="mb-6 flex flex-col gap-4 rounded-md border border-amber-300 bg-amber-50 text-amber-950 p-5 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-base font-semibold">
              {t("appBindingOnboarding.title")}
            </h2>
            <p className="max-w-2xl text-sm text-amber-900">
              {t("appBindingOnboarding.body")}
            </p>
          </div>
          <Button className="bg-amber-300 text-amber-950 hover:bg-amber-400" onClick={() => navigate("/console/app-bindings")}>
            <Link2 className="mr-2 h-4 w-4" />
            {t("appBindingOnboarding.cta")}
          </Button>
        </div>
      ) : null}

      {showSummaryCards || totpWarning || totpError ? (
        <div className="grid gap-4 xl:grid-cols-2">
          {/* Alerts */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex min-w-0 items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 text-muted-foreground" />
                <CardTitle asChild className="text-sm font-medium">
                  <h2>{t("alerts.title")}</h2>
                </CardTitle>
              </div>
              <span
                className="shrink-0 text-sm font-semibold text-foreground"
                aria-label={t("alerts.totalLabel", { value: alertTotalText })}
              >
                {alertTotalText}
              </span>
            </CardHeader>
            <CardContent>
              {totpWarning || totpError || hasStorageAlerts ||
              hasBindingAlerts ||
              isLoadingStorages ||
              data.isLoading ||
              storageListError ||
              data.error ? (
                <div className="space-y-1 text-sm">
                  {totpWarning ? <button type="button" className="block text-left text-amber-700 hover:underline" onClick={() => navigate("/console/account/security")}>メール・パスワードログインの二要素認証が未設定です。設定する</button> : null}
                  {totpError ? <p>二要素認証の設定状態を取得できませんでした。</p> : null}
                  {STORAGE_ALERT_AVAILABILITY_CODES.map((code) =>
                    availabilityCounts[code] > 0 ? (
                      <button
                        key={`storage:${code}`}
                        type="button"
                        className={`block text-left underline-offset-2 hover:underline ${alertTextClassForAvailability(
                          code,
                        )}`}
                        onClick={() =>
                          navigate(`/console/storages?availability=${code}`)
                        }
                      >
                        {t(`alerts.storage.${code}`, {
                          count: availabilityCounts[code],
                        })}
                      </button>
                    ) : null,
                  )}
                  {appAvailabilityAlerts.map(({ availabilityState, count }) => (
                      <button
                        key={`app-availability:${availabilityState}`}
                        type="button"
                        className={`block text-left underline-offset-2 hover:underline ${alertTextClassForAvailability(
                          availabilityState,
                        )}`}
                        onClick={() =>
                          navigate(`/console/app-bindings?availability=${availabilityState}`)
                        }
                      >
                        {t(`alerts.appAvailability.${availabilityState}`, {
                          count,
                        })}
                      </button>
                    ),
                  )}
                  {pluginReapplyPendingCount > 0 ? (
                    <button
                      type="button"
                      className="block text-left text-amber-700 underline-offset-2 hover:underline"
                      onClick={() =>
                        navigate(
                          "/console/app-bindings?settingReflectionState=plugin_reapply_pending",
                        )
                      }
                    >
                      {t("alerts.pendingReflection", {
                        count: pluginReapplyPendingCount,
                      })}
                    </button>
                  ) : null}
                  {pluginUpdateAvailableCount > 0 ? (
                    <button
                      type="button"
                      className="block text-left text-amber-700 underline-offset-2 hover:underline"
                      onClick={() =>
                        navigate("/console/app-bindings?pluginVersionState=update_available")
                      }
                    >
                      {t("alerts.pluginUpdateAvailable", {
                        count: pluginUpdateAvailableCount,
                      })}
                    </button>
                  ) : null}
                  {pluginVersionIncompatibleCount > 0 ? (
                    <button
                      type="button"
                      className="block text-left text-destructive underline-offset-2 hover:underline"
                      onClick={() =>
                        navigate("/console/app-bindings?blockedReason=plugin_version_incompatible")
                      }
                    >
                      {t("alerts.pluginVersionIncompatible", {
                        count: pluginVersionIncompatibleCount,
                      })}
                    </button>
                  ) : null}
                  {isLoadingStorages ? (
                    <p className="text-muted-foreground">
                      {t("alerts.storageLoading")}
                    </p>
                  ) : null}
                  {storageListError ? (
                    <p role="alert" className="text-destructive">
                      {t("alerts.storageError", { error: storageListError })}
                    </p>
                  ) : null}
                  {data.isLoading ? (
                    <p className="text-muted-foreground">
                      {t("alerts.appLoading")}
                    </p>
                  ) : null}
                  {data.error ? (
                    <p role="alert" className="text-destructive">
                      {t("alerts.appError", { error: data.error })}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t("alerts.noAlerts")}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Health */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex min-w-0 items-center gap-2">
                <HardDrive className="h-4 w-4 shrink-0 text-muted-foreground" />
                <CardTitle asChild className="text-sm font-medium">
                  <h2>{t("health.title")}</h2>
                </CardTitle>
              </div>
              <span
                className="shrink-0 text-sm font-semibold text-foreground"
                aria-label={t("health.totalLabel", { value: storageTotalText })}
              >
                {storageTotalText}
              </span>
            </CardHeader>
            <CardContent>
              {isLoadingStorages ? (
                <p className="text-sm text-muted-foreground">
                  {t("health.loading")}
                </p>
              ) : storageListError ? (
                <p role="alert" className="text-sm text-destructive">
                  {t("health.error", { error: storageListError })}
                </p>
              ) : (
                <div className="space-y-1 text-sm">
                  {DASHBOARD_AVAILABILITY_CODES.map((code) => (
                    <div key={code} className="flex justify-between">
                      <span>{t(`health.${code}`)}</span>
                      <span
                        className={`font-medium ${
                          code === "needs_action" && availabilityCounts[code] > 0
                            ? "text-destructive"
                            : code !== "available" && availabilityCounts[code] > 0
                              ? "text-amber-700"
                              : "text-slate-950"
                        }`}
                      >
                        {availabilityCounts[code]}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* kintone apps */}
          <Card className="xl:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex min-w-0 items-center gap-2">
                <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <CardTitle asChild className="text-sm font-medium">
                  <h2>{t("appHealth.title")}</h2>
                </CardTitle>
              </div>
              <span
                className="shrink-0 text-sm font-semibold text-foreground"
                aria-label={t("appHealth.totalLabel", { value: appTotalText })}
              >
                {appTotalText}
              </span>
            </CardHeader>
            <CardContent>
              {data.isLoading ? (
                <p className="text-sm text-muted-foreground">
                  {t("appHealth.loading")}
                </p>
              ) : data.error ? (
                <p role="alert" className="text-sm text-destructive">
                  {t("appHealth.error", { error: data.error })}
                </p>
              ) : data.appBindings.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("appHealth.empty")}
                </p>
              ) : (
                <div className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                        {APP_SUMMARY_AVAILABILITY_CODES.map((code) => (
                          <div key={code} className="flex justify-between">
                            <span>{t(`appHealth.${code}`)}</span>
                            <span
                              className={`font-medium ${
                                code === "needs_action" &&
                                appAvailabilitySummary[code] > 0
                                  ? "text-destructive"
                                  : code !== "available" &&
                                      appAvailabilitySummary[code] > 0
                                    ? "text-amber-700"
                                    : "text-slate-950"
                              }`}
                            >
                              {appAvailabilitySummary[code]}
                            </span>
                          </div>
                        ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
